// components/entities/pages/PortfolioOverviewHeader.tsx
'use client';

import { useCallback } from 'react';
import clsx from 'clsx';
import { useKernelStore } from '@/kernel/kernelStores';
import { useKernel } from '@/kernel/Kernel';
import { Building2, MapPin } from '@/components/ui/lucide-memo';
import { GLASS_FILL, GLASS_BORDER } from '@/components/ui/glass';
import { HeadlineStats, type HeadlineStat } from '@/components/portal/HeadlineStats';
import type { LeasingPortfolioDTO } from '#types/types';
import { EditableHeading } from '../EditableHeading';

interface PortfolioOverviewHeaderProps {
  portfolioId: string;
  className?: string;
}

const humanize = (value: string | null | undefined, fallback = ''): string => {
  if (!value || !value.trim()) return fallback;
  return value.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * Rich portfolio page header, large name, landlord/category identity,
 * location, a status pill and headline stats (Assets · Intent). Shares the
 * glass header card the other entity detail pages use instead of the flat
 * inventory row. (It was modelled on the asset page's header, which was
 * deleted with that page: an asset has no page of its own.)
 */
export function PortfolioOverviewHeader({ portfolioId, className }: PortfolioOverviewHeaderProps) {
  const { kernel } = useKernel();
  const lang = useKernelStore((s) => s.language);
  const data = useKernelStore((s) => s.inventory.Portfolio.registry[portfolioId]) as LeasingPortfolioDTO | undefined;

  const commitName = useCallback(
    (next: string) => kernel.current?.updateEntity('Portfolio', portfolioId, { name: next }),
    [kernel, portfolioId],
  );

  if (!data) return null;

  const identityLine = [humanize(data.type, lang === 'fr' ? 'Bailleur' : 'Landlord'), humanize(data.category, '')]
    .filter(Boolean)
    .join(' · ');
  const locationLabel = [data.city, data.country].filter(Boolean).join(', ');
  const statusLabel = humanize(data.status, '');
  const realCount = data.realPropertyCount ?? 0;
  const declCount = typeof data.propertyCount === 'number' ? data.propertyCount : null;

  const stats: HeadlineStat[] = [
    {
      value: realCount.toLocaleString(),
      label: lang === 'fr' ? 'Actifs' : 'Assets',
      sub:
        declCount != null && declCount !== realCount
          ? `${lang === 'fr' ? 'décl.' : 'decl.'} ${declCount.toLocaleString()}`
          : null,
    },
    {
      value: data.intentScore != null ? String(data.intentScore) : 'N/A',
      label: lang === 'fr' ? 'Intention' : 'Intent',
    },
  ];

  return (
    <div
      className={clsx(
        'mx-4 mt-4 flex flex-wrap items-center gap-x-8 gap-y-4 rounded-2xl px-6 py-5',
        GLASS_BORDER,
        GLASS_FILL,
        className,
      )}
    >
      {/* identity */}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <EditableHeading
            value={data.name}
            onCommit={commitName}
            className="block max-w-full truncate text-[32px] font-semibold leading-tight text-white"
          />
          {statusLabel && (
            <span className="rounded-full border border-white/15 bg-white/[0.06] px-2.5 py-0.5 text-[11px] text-white/70">
              {statusLabel}
            </span>
          )}
        </div>
        <div className="mt-2 flex items-center gap-2.5">
          <div className="flex h-8 w-8 flex-none items-center justify-center overflow-hidden rounded-md border border-white/10 bg-white/5">
            {data.logoUrl ? (
              <img src={data.logoUrl} alt={data.name} className="h-full w-full object-contain" />
            ) : (
              <Building2 size={15} className="text-white/35" />
            )}
          </div>
          <div className="min-w-0 leading-tight">
            <div className="truncate text-[12.5px] text-white/70">{identityLine || '–'}</div>
            {locationLabel && (
              <div className="mt-0.5 flex items-center gap-1 text-[11.5px] text-white/40">
                <MapPin size={11} className="shrink-0 opacity-70" />
                <span className="truncate">{locationLabel}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* headline stats */}
      <HeadlineStats stats={stats} />
    </div>
  );
}
